import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Upload, Camera, CheckCircle, Loader, DollarSign } from 'lucide-react';
import api from '../api/apiClient';

interface Property {
  id: number;
  address: string;
  leaseId: number;
}

const ITEM_CATEGORIES = [
  'Kitchen oven', 'Refrigerator', 'Dishwasher', 'Microwave',
  'Washing machine', 'Dryer', 'Air conditioner', 'Heater',
  'Hot water system', 'Living room sofa', 'Bed frame', 'Mattress',
  'Dining table', 'Carpet', 'Blinds', 'Other'
];

export default function SubmitReplacementRequest() {
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<number | null>(null);

  const [requestType, setRequestType] = useState<'REPLACEMENT' | 'UPGRADE'>('REPLACEMENT');
  const [itemType, setItemType] = useState('');
  const [customItem, setCustomItem] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [desiredOptions, setDesiredOptions] = useState('');

  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  const [willingToContribute, setWillingToContribute] = useState(false);
  const [contributionType, setContributionType] = useState<'PERCENT' | 'AMOUNT'>('PERCENT');
  const [contributionValue, setContributionValue] = useState('');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [requestId, setRequestId] = useState<number | null>(null);

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await api.get<{leases: any[]}>('/renter/onboarding/available-leases');
      const propertyList = response.leases?.map(lease => ({
        id: lease.propertyId,
        address: lease.propertyAddress,
        leaseId: lease.id
      })) || [];

      setProperties(propertyList);
      if (propertyList.length > 0) {
        setSelectedProperty(propertyList[0].leaseId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));

    if (validFiles.length + photos.length > 10) {
      alert('Maximum 10 files allowed');
      return;
    }

    setPhotos([...photos, ...validFiles]);

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
    setPhotoPreviews(photoPreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProperty) {
      setError('Please select a property');
      return;
    }

    const finalItemType = itemType === 'Other' ? customItem : itemType;
    if (!finalItemType) {
      setError('Please select or specify an item');
      return;
    }

    if (description.length < 10) {
      setError('Please provide a detailed description (at least 10 characters)');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Upload photos (simulated - in production, upload to S3)
      const photoUrls: string[] = [];
      for (const file of photos) {
        const url = `photos/${Date.now()}_${file.name}`;
        photoUrls.push(url);
      }

      const payload: any = {
        leaseId: selectedProperty,
        requestType,
        itemType: finalItemType,
        description,
        reason,
        photos: photoUrls
      };

      if (desiredOptions) {
        payload.desiredOptions = desiredOptions;
      }

      if (willingToContribute && contributionValue) {
        payload.renterContribution = {
          type: contributionType,
          value: parseFloat(contributionValue)
        };
      }

      const response = await api.post<{requestId: number}>('/replacements', payload);

      setRequestId(response.requestId);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0C4432' }}></div>
      </div>
    );
  }

  if (success && requestId) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#49c5b6' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#0C4432' }}>Request Submitted!</h2>
          <p className="mb-4" style={{ color: '#666' }}>
            Your {requestType.toLowerCase()} request has been submitted and will be reviewed by the property manager.
          </p>
          <div className="bg-gray-50 rounded-md p-4 mb-6">
            <p className="text-sm" style={{ color: '#666' }}>Request ID</p>
            <p className="text-2xl font-bold" style={{ color: '#0C4432' }}>#{requestId}</p>
          </div>
          <p className="text-sm mb-6" style={{ color: '#666' }}>
            You'll receive updates via email as your request progresses.
          </p>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-2 px-4 rounded-md text-white font-medium"
              style={{ backgroundColor: '#0C4432' }}
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-2 px-4 rounded-md border font-medium"
              style={{ borderColor: '#49c5b6', color: '#49c5b6' }}
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <RefreshCw className="w-8 h-8" style={{ color: '#0C4432' }} />
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#0C4432' }}>Request Replacement or Upgrade</h1>
            <p style={{ color: '#666' }}>Submit a request for item replacement or upgrade</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: '#FEE', color: '#DF6C4F' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Property Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
              Property *
            </label>
            <select
              value={selectedProperty || ''}
              onChange={(e) => setSelectedProperty(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select a property</option>
              {properties.map((property) => (
                <option key={property.leaseId} value={property.leaseId}>
                  {property.address}
                </option>
              ))}
            </select>
          </div>

          {/* Request Type */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium mb-3" style={{ color: '#222' }}>
              Request Type *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRequestType('REPLACEMENT')}
                className={`p-4 border-2 rounded-lg ${requestType === 'REPLACEMENT' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
              >
                <p className="font-medium" style={{ color: '#0C4432' }}>Replacement</p>
                <p className="text-sm" style={{ color: '#666' }}>Item is broken or faulty</p>
              </button>
              <button
                type="button"
                onClick={() => setRequestType('UPGRADE')}
                className={`p-4 border-2 rounded-lg ${requestType === 'UPGRADE' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
              >
                <p className="font-medium" style={{ color: '#0C4432' }}>Upgrade</p>
                <p className="text-sm" style={{ color: '#666' }}>Request better/newer item</p>
              </button>
            </div>
          </div>

          {/* Item Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
              Item *
            </label>
            <select
              value={itemType}
              onChange={(e) => setItemType(e.target.value)}
              className="w-full px-3 py-2 border rounded-md mb-3"
              required
            >
              <option value="">Select an item</option>
              {ITEM_CATEGORIES.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            {itemType === 'Other' && (
              <input
                type="text"
                value={customItem}
                onChange={(e) => setCustomItem(e.target.value)}
                placeholder="Specify the item..."
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            )}
          </div>

          {/* Description and Reason */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                Description *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the current condition..."
                className="w-full px-3 py-2 border rounded-md"
                rows={4}
                minLength={10}
                required
              />
              <p className="text-xs mt-1" style={{ color: '#666' }}>
                {description.length} / 10 minimum characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                Reason for {requestType === 'REPLACEMENT' ? 'Replacement' : 'Upgrade'}
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={requestType === 'REPLACEMENT' ?
                  "Why does this need to be replaced? (e.g., not working, damaged)" :
                  "Why would you like this upgraded? (e.g., energy efficiency, better features)"}
                className="w-full px-3 py-2 border rounded-md"
                rows={3}
              />
            </div>

            {requestType === 'UPGRADE' && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                  Desired Options/Features
                </label>
                <textarea
                  value={desiredOptions}
                  onChange={(e) => setDesiredOptions(e.target.value)}
                  placeholder="Specify preferred features, brands, or models..."
                  className="w-full px-3 py-2 border rounded-md"
                  rows={3}
                />
              </div>
            )}
          </div>

          {/* Photos/Videos */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium mb-3" style={{ color: '#222' }}>
              Photos/Videos *
            </label>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                type="file"
                id="photo-upload"
                accept="image/*,video/*"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
              />
              <label htmlFor="photo-upload" className="cursor-pointer flex flex-col items-center">
                <Camera className="w-12 h-12 mb-2" style={{ color: '#666' }} />
                <p className="font-medium" style={{ color: '#0C4432' }}>
                  Upload photos or videos
                </p>
                <p className="text-sm" style={{ color: '#666' }}>
                  Up to 10 files (images or videos)
                </p>
              </label>
            </div>

            {photoPreviews.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {photoPreviews.map((preview, index) => (
                  <div key={index} className="relative">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cost Contribution */}
          {requestType === 'UPGRADE' && (
            <div className="bg-white rounded-lg shadow p-6">
              <label className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  checked={willingToContribute}
                  onChange={(e) => setWillingToContribute(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="font-medium" style={{ color: '#222' }}>
                  I'm willing to contribute to the cost
                </span>
              </label>

              {willingToContribute && (
                <div className="space-y-3 pl-6">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setContributionType('PERCENT')}
                      className={`flex-1 py-2 px-4 border rounded-md ${contributionType === 'PERCENT' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                    >
                      Percentage (%)
                    </button>
                    <button
                      type="button"
                      onClick={() => setContributionType('AMOUNT')}
                      className={`flex-1 py-2 px-4 border rounded-md ${contributionType === 'AMOUNT' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                    >
                      Fixed Amount ($)
                    </button>
                  </div>

                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#666' }} />
                    <input
                      type="number"
                      value={contributionValue}
                      onChange={(e) => setContributionValue(e.target.value)}
                      placeholder={contributionType === 'PERCENT' ? '0-100' : '0.00'}
                      className="w-full pl-10 pr-3 py-2 border rounded-md"
                      min="0"
                      max={contributionType === 'PERCENT' ? '100' : undefined}
                      step={contributionType === 'PERCENT' ? '1' : '0.01'}
                    />
                  </div>

                  <p className="text-xs" style={{ color: '#666' }}>
                    {contributionType === 'PERCENT'
                      ? 'Percentage of total upgrade cost you\'ll pay'
                      : 'Fixed dollar amount you\'ll contribute'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 px-4 rounded-md text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: '#0C4432' }}
          >
            {submitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Submit Request
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

